import type { PickerRow } from 'api/canonicalPicker.types'
import { picksOf, SourceSelection } from 'slices/aiSegmentBuilder/picker'

/** As the server searches: a name by any part of it, a uid only whole, a group by its store UIDs too */
export const matchesSearch = (row: PickerRow, search: string): boolean => {
  const term = search.trim()
  if (!term) return true
  switch (row.kind) {
    case 'product':
      return row.name.toLowerCase().includes(term.toLowerCase())
    case 'uidGroup':
      return (
        row.name.toLowerCase().includes(term.toLowerCase()) ||
        row.uidGroup === term ||
        (row.storeUids ?? []).includes(term)
      )
    case 'storeUid':
      return (
        row.name.toLowerCase().includes(term.toLowerCase()) || row.uid === term
      )
    case 'unmatchedUid':
      return row.uid === term
  }
}

const holderOn = (page: PickerRow[], pick: PickerRow): PickerRow | undefined =>
  pick.kind === 'storeUid'
    ? page.find(
        row =>
          row.kind === 'uidGroup' &&
          row.provider === pick.provider &&
          (row.storeUids ?? []).includes(pick.uid)
      )
    : undefined

/**
 * What an open dropdown lists. Rows keep their place until it closes, so
 * nothing jumps under the cursor: the picks it opened with come first, in
 * that order, even once unticked, then the page without them, where a row
 * ticked meanwhile stays. A store UID pick whose global is on the page is
 * pinned as that global, so it never shows twice. A pick off the page, or
 * made meanwhile with no place on it, is narrowed by the same search.
 */
export const picksFirst = (
  page: PickerRow[],
  pickedWhenOpened: PickerRow[],
  selection: SourceSelection,
  search: string
): PickerRow[] => {
  const onPage = new Map(page.map(row => [row.id, row]))
  const shown = (pick: PickerRow): PickerRow | undefined =>
    onPage.get(pick.id) ??
    holderOn(page, pick) ??
    (matchesSearch(pick, search) ? pick : undefined)

  const pinned = new Map<string, PickerRow>()
  pickedWhenOpened.forEach(pick => {
    const row = shown(pick)
    if (row) pinned.set(row.id, row)
  })
  const opened = new Set(pickedWhenOpened.map(pick => pick.id))
  const madeSince = picksOf(selection).filter(
    pick =>
      !opened.has(pick.id) &&
      !onPage.has(pick.id) &&
      !holderOn(page, pick) &&
      matchesSearch(pick, search)
  )
  return [
    ...Array.from(pinned.values()),
    ...madeSince,
    ...page.filter(row => !pinned.has(row.id)),
  ]
}
